import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@contexts/AuthContext';
import { formatGivenName } from '@utils/stringUtils';
import PageLayout from '@components/layout/PageLayout';
import DashboardContainer from '@components/common/DashboardContainer';
import DashboardHeader from '@components/common/DashboardHeader';
import DashboardGrid from '@components/common/DashboardGrid';
import DashboardRow from '@components/common/DashboardRow';
import DashboardCard from '@components/common/DashboardCard';
import ErrorState from '@components/common/ErrorState';
import { AssessorLoadingChart, ProgramLeadMetrics, ProgramLeadQuickActions } from '@components/features/dashboard';
import { dashboardService } from '@services';
import { getCurrentMonth } from '@utils/assessorLoadingChart';
import type { AssessorLoadingData, ProgramLeadMetricsData, ProgramLeadQuickActionsData } from '@app-types';
import styles from './ProgramLeadDashboardPage.module.css';

const ProgramLeadDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const [month, setMonth] = useState(getCurrentMonth);

  const {
    data: metricsData,
    isLoading: metricsLoading,
    error: metricsError,
  } = useQuery<ProgramLeadMetricsData>({
    queryKey: ['program-lead-metrics'],
    queryFn: () => dashboardService.getProgramLeadMetrics(),
    refetchInterval: 60000,
    enabled: !!user,
  });

  const {
    data: quickActionsData,
    isLoading: quickActionsLoading,
    error: quickActionsError,
  } = useQuery<ProgramLeadQuickActionsData>({
    queryKey: ['program-lead-quick-actions'],
    queryFn: () => dashboardService.getProgramLeadQuickActions(),
    refetchInterval: 60000,
    enabled: !!user,
  });

  // A failed month only blanks the chart, so the Program Lead can pick another month without losing the page. No polling: the month is the user's choice.
  const {
    data: assessorLoadingData,
    isLoading: assessorLoadingLoading,
    isError: assessorLoadingError,
    refetch: refetchAssessorLoading,
  } = useQuery<AssessorLoadingData>({
    queryKey: ['program-lead-assessor-loading', month],
    queryFn: () => dashboardService.getProgramLeadAssessorLoading(month),
    refetchOnMount: 'always',
    enabled: !!user,
  });

  const error = metricsError || quickActionsError;

  if (error) {
    return (
      <PageLayout>
        <ErrorState message="Failed to load Program Lead dashboard data." />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className={styles.pageContainer}>
        <DashboardContainer>
          <DashboardHeader
            title={`Welcome back, ${user ? formatGivenName(user) : ''}!`}
            subtitle="Program-wide metrics and actions"
            role="Logged in as Program Lead"
            lastUpdated={`Last updated: ${new Date().toLocaleTimeString()}`}
          />
          <DashboardGrid>
            <DashboardRow>
              <ProgramLeadMetrics data={metricsData} isLoading={metricsLoading} />
            </DashboardRow>
            <DashboardRow>
              <DashboardCard title="Quick Actions">
                <ProgramLeadQuickActions data={quickActionsData} isLoading={quickActionsLoading} />
              </DashboardCard>
            </DashboardRow>
            <DashboardRow>
              <DashboardCard>
                <AssessorLoadingChart
                  month={month}
                  onMonthChange={setMonth}
                  data={assessorLoadingData}
                  isLoading={assessorLoadingLoading}
                  isError={assessorLoadingError}
                  onRetry={() => refetchAssessorLoading()}
                />
              </DashboardCard>
            </DashboardRow>
          </DashboardGrid>
        </DashboardContainer>
      </div>
    </PageLayout>
  );
};

export default ProgramLeadDashboardPage;
